import { AgendaAgendamentoPaciente } from '../../../../interfaces/agenda';
import { PatientAdvance } from '../../../../interfaces/payment';
import { adiantamentoEstornado } from '../../../../utils/pagamento-avulso.util';

export const HISTORICO_MESES_PASSADO = 12;
export const HISTORICO_MESES_FUTURO = 3;
export const HISTORICO_MAX_LINHAS = 20;

export interface AgendaPacienteEscolhido {
  /** Texto: CD_PESSOA_FISICA é VARCHAR2 no Tasy. */
  cdPessoaFisica: string;
  nome: string;
  cpf: string;
}

export interface LinhaHistorico {
  diaMes: string;
  hora: string;
  titulo: string;
  sub: string;
  chip: 'consulta' | 'exame' | 'retorno' | 'futuro';
  chipTexto: string;
  status: string;
  corStatus: 'verde' | 'vermelho' | 'amarelo' | 'cinza';
}

export function corStatusHistorico(st: string | null | undefined): LinhaHistorico['corStatus'] {
  if (['AD', 'E', 'FI'].includes(st ?? '')) return 'verde';
  if (st === 'C') return 'vermelho';
  if (['N', 'CN', 'M'].includes(st ?? '')) return 'amarelo';
  return 'cinza';
}

function isoDeBr(d: string | null | undefined): string {
  const p = (d ?? '').split('/');
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : '';
}

/** Cancelado no futuro não é "a realizar": a marca diz que o paciente já tem aquilo marcado. */
export function linhaHistorico(a: AgendaAgendamentoPaciente, hojeIso: string): LinhaHistorico {
  const iso = isoDeBr(a.dtAgendaFmt);
  const futuro = !!iso && iso > hojeIso && a.ieStatusAgenda !== 'C';
  let chip: LinhaHistorico['chip'] = 'consulta';
  if (futuro) chip = 'futuro';
  else if (a.tipo === 'EXAME') chip = 'exame';
  else if ((a.dsClassif ?? '').toLowerCase().includes('retorno')) chip = 'retorno';
  const textos = { consulta: 'CONSULTA', exame: 'EXAME', retorno: 'RETORNO', futuro: 'A REALIZAR' };
  const oque = (a.tipo === 'EXAME' ? a.dsExame : a.dsEspecialidade) || a.dsAgenda || '';
  const partes = (a.dtAgendaFmt ?? '').split('/');
  return {
    diaMes: partes.length === 3 ? `${partes[0]}/${partes[1]}/${partes[2].slice(2)}` : a.dtAgendaFmt ?? '',
    hora: a.hrInicio ?? '',
    titulo: a.nmMedico ? `${oque} — ${a.nmMedico}` : oque,
    sub: [a.nmEstabelecimento, a.dsConvenio].filter(Boolean).join(' · '),
    chip,
    chipTexto: textos[chip],
    status: a.dsStatusAgenda ?? '',
    corStatus: corStatusHistorico(a.ieStatusAgenda),
  };
}

/** Janela do histórico: um ano para trás e um trimestre para a frente (o que já está marcado). */
export function janelaHistorico(hoje: Date): { dtInicio: string; dtFim: string } {
  const iso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const ini = new Date(hoje.getFullYear(), hoje.getMonth() - HISTORICO_MESES_PASSADO, hoje.getDate());
  const fim = new Date(hoje.getFullYear(), hoje.getMonth() + HISTORICO_MESES_FUTURO, hoje.getDate());
  return { dtInicio: iso(ini), dtFim: iso(fim) };
}

/**
 * Saldo de adiantamento disponível, com as regras do Histórico do Paciente: estornado e
 * transferido para outra pessoa não contam.
 */
export function saldoDisponivel(adiantamentos: PatientAdvance[], cdPessoaFisica: string): number {
  return adiantamentos.reduce((s, a) => {
    const destino = (a.cdPessoaAdiant ?? '').trim();
    if (adiantamentoEstornado(a) || (destino !== '' && destino !== cdPessoaFisica)) return s;
    return s + Math.max(a.vlSaldo ?? 0, 0);
  }, 0);
}
